import React, { useEffect, useState } from "react";
import { TriggerStepCreate } from "@dots-projects/types";
import { useForm } from "react-hook-form";
import produce from "immer";
import { saveDraft, updateStep } from "./reducer/actionCreators";
import { useTriggerEditorStateDispatch } from "./TriggerEditor";
import invariant from "ts-invariant";
import { StepFormWithCommonParts } from "./TriggerStepForm";
import { FormLabel } from "./FormLabel";
import { Radio, RadioChangeEvent, Space } from "antd";
import { Input } from "./Input";

/**
 * Renders the form for a "Send message" step. When the form is submitted, generate the step data and publish an update action to add a new step to the trigger flow.
 */
export function StepFormShowModal({ step }: { step: TriggerStepCreate }) {
  invariant(step.type === "open_modal");
  // Discord limitation for title and label text  https://discord.com/developers/docs/interactions/receiving-and-responding#interaction-response-object-modal
  const MAX_LABEL_LENGTH = 45;

  const defaultValues = {
    name: step.name,
    modal_box_title: step.configs.title,

    // Only handling for one modal field for now.
    modal_label: step.configs.fields[0].label,
    input_type: step.configs.fields[0].style,
  };

  const form = useForm({
    defaultValues,
  });
  const { handleSubmit, register } = form;
  const { state, dispatch, onPersist } = useTriggerEditorStateDispatch();
  const readOnly = !state.trigger.current.isDraft;

  const onSubmit = (data: any) => {
    // This uses the immer library to create a new copy of step (with updates)
    // without affecting the original step.
    const newStep = produce(step, (stepDraft) => {
      stepDraft.name = data.name;
      stepDraft.configs.title = data.modal_box_title;
      stepDraft.configs.fields[0].label = data.modal_label;
      stepDraft.configs.fields[0].style = style;
    });

    dispatch(updateStep(newStep));
    dispatch(saveDraft(onPersist));
  };

  const [style, setStyle] = useState(defaultValues.input_type);

  const onStyleChange = (e: RadioChangeEvent) => {
    setStyle(e.target.value);
  };

  return (
    <StepFormWithCommonParts
      onSubmit={handleSubmit(onSubmit)}
      form={form}
      step={step}
    >
      <FormLabel label="Modal box title">
        <Input
          type={"text"}
          disabled={Boolean(state.editor.isPersisting)}
          readOnly={readOnly}
          style={{ width: "100%" }}
          maxLength={MAX_LABEL_LENGTH}
          {...register("modal_box_title")}
        />
      </FormLabel>
      <FormLabel label="Modal label">
        <Input
          type={"text"}
          readOnly={readOnly}
          maxLength={MAX_LABEL_LENGTH}
          disabled={Boolean(state.editor.isPersisting)}
          style={{ width: "100%" }}
          {...register("modal_label")}
        />
      </FormLabel>
      <FormLabel label="Input type">
        <Radio.Group onChange={onStyleChange} value={style}>
          <Space direction="vertical">
            <Radio value="short">Short</Radio>
            <Radio value="paragraph">Paragraph</Radio>
          </Space>
        </Radio.Group>
      </FormLabel>
    </StepFormWithCommonParts>
  );
}
